import { computed, reactive, toValue } from 'vue';
import type { MaybeRefOrGetter } from 'vue';
import type { CustomFieldProps } from '@satellite/components/custom_forms/types';

/**
 * Normalized props interface for internal use in field components
 */
export interface NormalizedCustomFieldProps {
  fieldIcon: string;
  displayLabel: boolean;
  hideDetails: boolean | 'auto';
  outlined: boolean;
  singleLine: boolean;
  persistentPlaceholder: boolean;
  timezone?: string;
  militaryTimeEnabled?: boolean;
  readOnly: boolean;
  /** Passed to CustomFieldInputBase as :required */
  required: boolean;
}

/**
 * Composable for normalizing and providing default values for custom field props.
 * Returns a reactive object (not a Ref) so template type-checking works when
 * passing to child components (e.g. normalizedProps.readOnly).
 *
 * @param props - Raw props from the component. Pass a getter (`() => ({ ...props })`) so the
 *   normalized values keep tracking the source props; a plain object is read once, at setup.
 * @returns Normalized props with defaults applied
 */
/**
 * modelValue is excluded because nothing here reads it, and field types that round-trip a richer
 * value than the shared scalar union — CustomFieldTimestamp emits a Luxon DateTime — narrow it on
 * their own props. Requiring the shared shape would reject those without buying anything.
 */
type NormalizableCustomFieldProps = Omit<CustomFieldProps, 'modelValue'>;

export function useCustomFieldProps(props: MaybeRefOrGetter<NormalizableCustomFieldProps>) {
  const normalizedProps: NormalizedCustomFieldProps = reactive({
    fieldIcon: computed(() => toValue(props).fieldIcon ?? ''),
    displayLabel: computed(() => toValue(props).displayLabel ?? true),
    hideDetails: computed(() => toValue(props).hideDetails ?? 'auto'),
    outlined: computed(() => toValue(props).outlined ?? false),
    singleLine: computed(() => toValue(props).singleLine ?? false),
    persistentPlaceholder: computed(() => toValue(props).persistentPlaceholder ?? true),
    timezone: computed(() => toValue(props).timezone),
    militaryTimeEnabled: computed(() => toValue(props).militaryTimeEnabled),
    readOnly: computed(() => toValue(props).readOnly ?? false),
    required: computed(() => {
      const rawProps = toValue(props);
      return rawProps.required ?? rawProps.field?.required ?? false;
    })
  });

  return {
    normalizedProps
  };
}
